import { Col, Row } from "react-bootstrap";
import {
  competitionGroupCompetitionsMappingsApi,
  competitionsMasterDataApi,
} from "@/apis/apis";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useGet, usePut } from "@/apiHooks/HttpFunctions";

import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import CompetitionGroupCompetitionInitialValues from "@/data/WebDataManagement/Mapping/CompetitionGroupCompetition/initial-values";
import CompetitionGroupCompetitionSchema from "@/schema/WebDataManagement/Mapping/CompetitionGroupCompetition/CompetitionGroupCompetitionSchema";
import { LIST_MODE } from "@/constant/Lists/listMode";
import RenderToast from "@/components/RenderToast";
import axiosInstance from "@/Axios/ConfiguredAxios";
import { debounce } from "@mui/material";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";

const CompetitionGroupCompetitionModal = forwardRef((_, ref) => {
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [saveError, setSaveError] = useState(null);
  const resetError = useCallback(() => {
    setSaveError(null);
  }, []);

  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${competitionGroupCompetitionsMappingsApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const listApi = `${competitionsMasterDataApi}?mode=${LIST_MODE.SELECT}&filter[keyword]=`;

  useEffect(() => {
    if (!modalState.isOpen) return;

    const fetchDefaultOptions = async () => {
      setLoadingOptions(true);
      const response = await axiosInstance.get(listApi);

      if (response) {
        const options =
          response.data.data.map((val) => ({
            label: `${val.code} - ${val.name}${
              val.competition_group_id ? "*" : ""
            }`,
            value: val.id,
          })) ?? [];

        const selectedCompetitions = showData?.data?.competitions || [];
        selectedCompetitions.forEach((competition) => {
          if (!options.some((opt) => opt.value === competition.id)) {
            options.unshift({
              label: `${competition.code} - ${competition.name}${
                competition.competition_group_id ? "*" : ""
              }`,
              value: competition.id,
            });
          }
        });

        setDefaultOptions(options);
      }

      setLoadingOptions(false);
    };

    fetchDefaultOptions();
  }, [listApi, modalState, showData]);

  const debouncedFetchOptions = debounce(async (input, callback) => {
    setLoadingOptions(true);
    const response = await axiosInstance.get(`${listApi}${input}`);

    if (response) {
      const newOptions =
        response.data.data
          .filter((item) => item.id !== modalState.id)
          .map((item) => ({
            label: `${item.code} - ${item.name}${
              item.competition_group_id ? "*" : ""
            }`,
            value: item.id,
          })) ?? [];

      // Append new options to defaultOptions if not already present
      setDefaultOptions((prev) => {
        const existingIds = prev.map((opt) => opt.value);
        return [
          ...prev,
          ...newOptions.filter((option) => !existingIds.includes(option.value)),
        ];
      });

      callback(newOptions);
    }

    setLoadingOptions(false);
  }, 500);

  const getSelectedOption = () => {
    if (!formik.values.competition_ids?.length) return [];
    return formik.values.competition_ids.map((id) => {
      // Attempt to find the option in the defaultOptions
      const foundOption = defaultOptions.find((opt) => opt.value === id);
      if (foundOption) {
        return foundOption;
      }

      // Fallback: Dynamically create the option
      const matchingCompetition = showData?.data?.competitions.find(
        (v) => v.id === id
      );
      return {
        label: matchingCompetition
          ? `${matchingCompetition.code} - ${matchingCompetition.name}${
              matchingCompetition.competition_group_id ? "*" : ""
            }`
          : `Unknown (${id})`,
        value: id,
      };
    });
  };

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Competition Group - Competition ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${competitionGroupCompetitionsMappingsApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });

  useEffect(() => {
    setSaveError(saveEditError);
  }, [saveEditError]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: CompetitionGroupCompetitionInitialValues(
      modalState.id ? showData : null
    ),
    validationSchema: CompetitionGroupCompetitionSchema(),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    saveEdit(JSON.stringify(values));
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  if (!showData) return null;

  return (
    <BasicModal
      header={
        modalState.id
          ? "Edit Competition Group - Competition"
          : "Add Competition Group - Competition"
      }
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            saveError={saveError}
            label="Competition Group"
            disabled={true}
            value={`${showData.data.code} - ${showData.data.name}`}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            hintText="Asterisk symbol (*) means it is already mapped to another Competition Group.
                  Selecting it will replace the Competition Group it is already associated with."
            isMulti={true}
            label="Competitions"
            placeholder="No competition selected. Type to search..."
            defaultOptions={defaultOptions}
            loadOptions={debouncedFetchOptions}
            isLoading={loadingOptions}
            value={getSelectedOption()}
            onChange={(selected) => {
              if (selected) {
                // Add selected option to defaultOptions if not already present
                setDefaultOptions((prev) => {
                  if (!prev.some((opt) => opt.value === selected.value)) {
                    return [
                      ...prev,
                      { label: selected.label, value: selected.value },
                    ];
                  }
                  return prev;
                });

                // Update formik value
                formik.setFieldValue(
                  "competition_ids",
                  selected.map((sel) => sel.value)
                );
              } else {
                formik.setFieldValue("competition_ids", []);
              }
            }}
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default CompetitionGroupCompetitionModal;
